import { compare as compareVersions } from 'compare-versions';
import { inject } from 'inversify';

import { META_DATA_PROPERTIES } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { IMetaData } from '@/entities/MetaData';
import { CancellationReasonManager } from '@/managers/CancellationReasonManager';
import { FeaturesManager } from '@/managers/FeaturesManager';
import { GenerationsManager } from '@/managers/GenerationsManager';
import { LateReasonsManager } from '@/managers/LateReasonsManager';
import { MiscTimestampsManager } from '@/managers/MiscTimestampsManager';
import { PriorityManager } from '@/managers/PriorityManager';
import { TripNoteTypeManager } from '@/managers/TripNoteTypeManager';
import { UnassignReasonsManager } from '@/managers/UnassignReasonsManager';
import { VehicleTypeManager } from '@/managers/VehicleTypeManager';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { MetaDataStore } from '@/stores/MetaDataStore';
import { fetchJson } from '@/utils/fetchJson';
import type { ICallTakingRequiredFieldsMsg } from '~/api/messages/ICallTakingRequiredFieldsMsg';
import type { IListItemResMsg } from '~/api/messages/IListItemResMsg';

@SignalrListener
@service(MetaDataManager)
export class MetaDataManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(CancellationReasonManager) protected cancellationReasonManager: CancellationReasonManager;
  @inject(FeaturesManager) protected featuresManager: FeaturesManager;
  @inject(GenerationsManager) protected generationsManager: GenerationsManager;
  @inject(LateReasonsManager) protected lateReasonsManager: LateReasonsManager;
  @inject(MiscTimestampsManager) protected miscTimestampsManager: MiscTimestampsManager;
  @inject(PriorityManager) protected priorityManager: PriorityManager;
  @inject(UnassignReasonsManager) protected unassignReasonsManager: UnassignReasonsManager;
  @inject(VehicleTypeManager) protected vehicleTypeManager: VehicleTypeManager;
  @inject(MetaDataStore) protected metaDataStore: MetaDataStore;
  @inject(TripNoteTypeManager) protected tripNoteTypesManager: TripNoteTypeManager;

  updateMetaData(response: IMetaData) {
    this.metaDataStore.update(response);
  }

  findByListType(listType: string) {
    return this.metaDataStore.find(listType);
  }

  get callTakingRequiredFields() {
    return this.metaDataStore.allCallTakingRequiredFields;
  }

  async hasNewBridgeVersion() {
    const versionMeta = this.metaDataStore.find(META_DATA_PROPERTIES.VERSION) as IMetaData | undefined;
    const currentVersion = versionMeta?.items?.[0]?.value;
    const { version: latestBridgeVersion } = await fetchJson('/downloads/version.json') as { version: string };
    return currentVersion ? compareVersions(currentVersion, latestBridgeVersion, '<') : false;
  }

  @SignalrMessage('ListItemMsg')
  protected onListItemMsg(response: IListItemResMsg) {
    const { listType, items } = response;

    switch (listType) {
      case META_DATA_PROPERTIES.FEATURES: {
        this.featuresManager.updateFeatures(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.GENERATIONS: {
        this.generationsManager.updateGenerations(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.LATE_REASONS: {
        this.lateReasonsManager.updateLateReasons(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.CANCELLATION_REASONS: {
        this.cancellationReasonManager.updateCancellationReasons(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.MISC_TIMESTAMPS: {
        this.miscTimestampsManager.updateMiscTimestamps(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.UNASSIGN_REASONS: {
        this.unassignReasonsManager.updateUnassignReasons(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.VEHICLE_TYPE: {
        this.vehicleTypeManager.updateVehicleTypes(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.TRIP_NOTE_TYPES: {
        this.tripNoteTypesManager.updateTripNoteTypes(Array.isArray(items) ? items : []);
        break;
      }
      case META_DATA_PROPERTIES.PRIORITY: {
        // Ignore here due to not getting all the priority info
        break;
      }
      default: {
        // Fallback for unhandled list types
        this.updateMetaData({ listType, items: Array.isArray(items) ? items : [] });
        break;
      }
    }
  }

  @SignalrMessage('CallTakingRequiredFieldsMsg')
  protected onCallTakingRequiredFieldsMsg(response: ICallTakingRequiredFieldsMsg) {
    this.metaDataStore.updateCallTakingRequiredFields(response.fields);
  }
}
